import PropTypes from 'prop-types';
import { NavLink as RouterLink } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  Avatar,
} from '@mui/material';

import navConfig from './nav-config';
import useAuth from '../../auth/useAuth';

export const DRAWER_WIDTH = 280;

function NavList({ items, onNavigate }) {
  const { role } = useAuth();

  return (
    <List disablePadding sx={{ px: 1 }}>
      {items
        .filter((item) => !item.roles || item.roles.includes(role))
        .map((item) => (
          <ListItemButton
            key={item.path}
            component={RouterLink}
            to={item.path}
            end={item.path === '/admin'}
            onClick={onNavigate}
            sx={{
              mb: 0.5,
              '&.active': {
                color: 'primary.main',
                bgcolor: 'action.selected',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.title} slotProps={{ primary: { variant: 'body2' } }} />
          </ListItemButton>
        ))}
    </List>
  );
}

NavList.propTypes = {
  items: PropTypes.array.isRequired,
  onNavigate: PropTypes.func,
};

function SidebarContent({ onNavigate }) {
  const { user } = useAuth();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', px: 2.5, py: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 700 }}>PC</Avatar>
        <Box>
          <Typography variant="subtitle1" noWrap>
            PalaciComputers
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            Panel de administración
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {navConfig
          .filter((section) => !section.roles || section.roles.includes(user?.role))
          .map((section) => (
            <Box key={section.subheader} sx={{ mb: 2 }}>
              <Typography
                variant="overline"
                color="text.disabled"
                sx={{ px: 2.5, mb: 1, display: 'block' }}
              >
                {section.subheader}
              </Typography>
              <NavList items={section.items} onNavigate={onNavigate} />
            </Box>
          ))}
      </Box>
    </Box>
  );
}

SidebarContent.propTypes = {
  onNavigate: PropTypes.func,
};

export default function Sidebar({ mobileOpen, onCloseMobile }) {
  return (
    <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
      {/* Drawer temporal para pantallas pequenas */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onCloseMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, borderRightStyle: 'dashed' },
        }}
      >
        <SidebarContent onNavigate={onCloseMobile} />
      </Drawer>

      {/* Drawer permanente en escritorio */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, borderRightStyle: 'dashed' },
        }}
        open
      >
        <SidebarContent />
      </Drawer>
    </Box>
  );
}

Sidebar.propTypes = {
  mobileOpen: PropTypes.bool,
  onCloseMobile: PropTypes.func,
};
